import React, { useState } from 'react'

import { useBreakpoint } from 'slab'
import { type PayrollProviderConfig } from 'utils/web_api.schemas'

import { cn } from '../../utils'

const PayrollConnectionGridItem = ({
  children,
  ...props
}: {
  children?: React.ReactNode;
  onClick?: () => Promise<void> | void;
} & React.HTMLAttributes<HTMLButtonElement>) => {
  const [isPending, setIsPending] = useState(false)

  return (
    <li className='block'>
      <button
        {...props}
        disabled={isPending}
        onClick={async () => {
          setIsPending(true)
          await props.onClick?.()
          setIsPending(false)
        }}
        className={cn(
          'block w-full border border-gray-200 active:border-thatch-500 bg-white hover:bg-gray-50 active:bg-thatch-50 shadow hover:shadow-md active:shadow rounded-lg p-4 hover:cursor-pointer outline-2 outline-offset-4 outline-transparent focus-visible:outline-thatch-500 transition',
          {
            'cursor-default bg-thatch-50 border-thatch-500': isPending,
          },
          props.className
        )}
        type='button'
      >
        {children}
      </button>
    </li>
  )
}

interface PayrollProviderGridProps {
  promotedProviders: PayrollProviderConfig[];
  onSelectProvider: (provider: PayrollProviderConfig) => Promise<void>;
  onClickOther: () => void;
}

export const PayrollProviderGrid: React.FC<PayrollProviderGridProps> = ({
  promotedProviders,
  onSelectProvider,
  onClickOther
}) => {
  const { breakpointIsAtLeast } = useBreakpoint()

  // Determine grid class based on provider count
  let gridClass = breakpointIsAtLeast('xs') ? 'grid-cols-4' : 'grid-cols-2'
  if (breakpointIsAtLeast('xs')) {
    if ((promotedProviders.length + 1) % 3 === 0) {
      gridClass = 'grid-cols-3'
    } else if ((promotedProviders.length + 1) % 5 === 0) {
      gridClass = 'grid-cols-5'
    }
  }

  return (
    <ul className={`grid ${gridClass} gap-2 md:gap-6 mt-6`}>
      {promotedProviders.map(provider => (
        <PayrollConnectionGridItem
          key={provider.id}
          data-testid={`promoted-payroll-provider-${provider.id}`}
          onClick={() => onSelectProvider(provider)}
        >
          <div
            className='flex items-center justify-center h-12 bg-no-repeat bg-contain bg-center'
            aria-label={provider.name}
            style={{ backgroundImage: `url(${provider.logo_image_url})` }}
          />
        </PayrollConnectionGridItem>
      ))}
      <PayrollConnectionGridItem
        onClick={onClickOther}
        className='flex items-center justify-center h-full'
      >
        Other
      </PayrollConnectionGridItem>
    </ul>
  )
}
